/* Scriptorium Landing — thin composer of the sibling primitives that
 * live in `./scriptorium/*`. This file focuses on layout, state, and
 * data fetching; visual primitives (rail, bookmark, mode slider,
 * bookshelf, gateway, typewriter, unread stack) each own their own
 * tokens & JSX.
 *
 * The centrepiece: a "reveal stage" section where the The Loom
 * gateway and the leather bookshelf sit on top of each other. A
 * floating ModeSlider hovers over the stage and cross-fades / slides
 * the two panels based on its value — drag left to reveal the Pro
 * Writer free-to-try offer, drag right to reveal the shelf.
 *
 * Every original data-testid (`cb-nav-*`, `cb-cta-*`, `cb-room-*`,
 * `authors-portal-*`) is preserved so tours, tests and admin panels
 * keep working.
 */
import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, KeyRound, Store, ShieldCheck, HandCoins, Newspaper, X,
} from 'lucide-react';

import { P, FONTS, GRAIN_URL, useScriptoriumFonts } from './scriptorium/palette';
import { trackLandingEvent } from '@/lib/landingAnalytics';


import TypewriterPane     from './scriptorium/TypewriterPane';
import NotepadTypewriter  from './scriptorium/NotepadTypewriter';
import PwaInstallCard from '@/components/PwaInstallCard';
import ReadInReaderBtn from '@/components/ReadInReaderBtn';
import LandingBookCarousel from './scriptorium/LandingBookCarousel';
import ProWriterGateway   from './scriptorium/ProWriterGateway';
import UnreadStackFooter  from './scriptorium/UnreadStackFooter';
import PoweredByEmergent  from './PoweredByEmergent';
import LanguageCapsule    from '@/components/LanguageCapsule';    // Feb-2026 · i18n
import useBusinessUnread  from '@/lib/useBusinessUnread';
import { captureStickyUtms } from '@/lib/utmTracking';
import { useAuth } from '@/lib/auth_context';
import WelcomeVideoBand from './WelcomeVideoBand';
import PublicNavBar from './PublicNavBar';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useHreflang } from '@/lib/useHreflang';  // 2026-02 · hreflang SEO/GEO
import StructuredData, {
  organizationLd, websiteLd, softwareApplicationLd, videoObjectLd,
} from '@/components/seo/StructuredData';          // 2026-02 · Schema.org GEO

const THINKER_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/6b7c1a5985a963925912229917e3ea18257cb9e674967fcd87a71b1e1ffd1319.jpeg';

/** Tasteful "Coming Soon" overlay for the Non-Fiction carousel. */
function NonfictionComingSoonModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(26,18,10,0.72)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="nonfiction-coming-soon-modal"
    >
      <div
        className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-2xl shadow-2xl"
        style={{
          backgroundColor: '#fdf6e4',
          border: `1px solid ${P.gold}88`,
          boxShadow: `0 32px 72px -20px rgba(90,28,31,0.45)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full
                     bg-[#2a1f18]/60 text-[#e8c87c] hover:bg-[#2a1f18]/80 transition-colors"
          data-testid="nonfiction-modal-close"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Image */}
        <div className="relative overflow-hidden" style={{ maxHeight: '380px' }}>
          <img
            src={THINKER_IMAGE}
            alt="The Thinker — Rodin's contemplative bronze sculpture on a clean modern backdrop"
            className="w-full object-cover object-center"
            style={{ maxHeight: '380px' }}
          />
          {/* Gradient overlay so text below reads cleanly */}
          <div
            className="absolute bottom-0 left-0 right-0 h-24"
            style={{ background: 'linear-gradient(to bottom, transparent, #fdf6e4)' }}
          />
        </div>

        {/* Copy */}
        <div className="px-8 pb-8 pt-4 text-center">
          <p
            className="mb-1 text-[10px] uppercase tracking-[0.28em]"
            style={{ color: P.oxblood, fontFamily: FONTS.type }}
          >
            Crown &amp; Rose Non-Fiction
          </p>
          <h2
            className="mb-3 text-2xl leading-tight"
            style={{ fontFamily: FONTS.display, fontWeight: 700, color: P.ink }}
          >
            Written Opuses
          </h2>
          <p
            className="text-[13px] leading-relaxed"
            style={{ color: P.inkSoft, fontFamily: FONTS.body }}
          >
            A curated shelf of thoughtful non-fiction — essays, memoirs, and ideas
            that stay with you long after the last page. Our first titles are in
            production and will be listed here soon.
          </p>
          <div
            className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2 text-[12px] font-semibold"
            style={{ backgroundColor: P.oxblood, color: P.paper, fontFamily: FONTS.display }}
          >
            Coming Soon
          </div>
        </div>
      </div>
    </div>
  );
}

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// 2026-10 — Mirrors backend DEFAULT_SEO_HOME (routes_seo.py) so admin
// edits to the homepage's SEO record actually take effect client-side —
// previously this page never called useSeoMeta at all, so Admin → SEO
// changes for "/" were silently dead.
const SEO_FALLBACK = {
  title:       'Novel Writing Software for Authors — Crown & Rose',
  description: 'Multi-model AI co-author (Claude, GPT, Gemini) for long-form fiction. Draft chapters, run character bibles, export manuscripts, sell in the store.',
  keywords:    'AI novel writing software, AI ghostwriter, long-form fiction AI, novel co-author, character bible tool, chapter beat sheet generator, author business dashboard, indie author platform, novel writing studio, scrivener alternative, AI writing app for novelists, sell ebooks direct, self-publishing platform, book marketing dashboard',
  og_image:    'https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/ac780d6d9643e90da4dac47b8cdc0c57e2a4b22688c982a7c8b6d317c475e31a.jpeg',
};

function CommunityFundTicker() {
  const { t } = useTranslation();
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    let active = true;
    fetch(`${API}/community-publishing-fund`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('fund unavailable')))
      .then((data) => { if (active) setBalance(Number(data.balance_usd || 0)); })
      .catch(() => { if (active) setBalance(null); });
    return () => { active = false; };
  }, []);
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="landing-who-we-are-fund-group">
      <Link to="/diversity-sustainability" data-testid="landing-who-we-are-link" className="font-semibold opacity-85 transition-opacity duration-150 hover:opacity-100" style={{ color: P.inkSoft }}>{t('landing.whoWeAre')}</Link>
      <Link to="/diversity-sustainability" data-testid="landing-community-fund-ticker" className="inline-flex min-h-6 items-center gap-1 border px-2 py-0.5 font-mono tabular-nums transition-colors duration-200 hover:bg-white/40" style={{ borderColor: `${P.goldDeep}88`, color: P.leather, backgroundColor: `${P.paper}aa` }} aria-label={balance == null ? 'Community Publishing Fund' : `Community Publishing Fund balance ${balance.toFixed(2)} dollars`}>
        <HandCoins className="h-3 w-3" aria-hidden="true" />
        <span>{t('landing.fund')}</span>
        <strong data-testid="landing-community-fund-balance">{balance == null ? '—' : balance.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
      </Link>
    </div>
  );
}

export default function ScriptoriumLanding({
  content: contentProp,
  theme: _theme,          // hardcoded Scriptorium palette; theme colours are intentionally ignored
  grand_opening: grandOpeningProp,          // Feb-2026 — Dec-15 marquee state (from /api/landing)
  interactive = true,
  onEmailSignin,
  onStartTrial,
  onDemo,
  demoBusy = false,
  onAuthorsPortal,
}) {
  useScriptoriumFonts();
  useSeoMeta('/', SEO_FALLBACK);
  useHreflang();   // 2026-02 · injects <link rel="alternate" hreflang="xx"> for all 6 locales
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.language || 'en').toLowerCase().startsWith('en');
  // 2026-02 · Schema.org lang for GEO (AI engine classification)
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);
  const siteUrl = window.location.origin;
  // Locale-aware paths
  const NON_EN = ['fr', 'de', 'es', 'pt', 'zh'];
  const blogPath = NON_EN.includes(lang) ? `/${lang}/blog` : '/blog';

  // Non-Fiction "Coming Soon" modal state
  const [nonfictionModalOpen, setNonfictionModalOpen] = useState(false);

  // 2026-02 · Regionalized content: for non-English locales, fetch
  // culturally-adapted copy generated by Claude Haiku.
  // Falls back gracefully to t() if not yet generated.
  const [regionalContent, setRegionalContent] = useState(null);
  useEffect(() => {
    if (isEnglish || !interactive) return;
    const locale = (i18n.language || 'en').slice(0, 2);
    let active = true;
    fetch(`${API}/landing/localized-content?locale=${locale}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d?.content && Object.keys(d.content).length > 0) setRegionalContent(d.content); })
      .catch(() => {});
    return () => { active = false; };
  }, [isEnglish, i18n.language, interactive]);

  // 2026-02 · Locale-specific Mei intro video (Emergent Object Storage).
  // Falls back to the global content?.mei_intro_video_url if not set.
  const [localeVideoUrl, setLocaleVideoUrl] = useState(null);
  useEffect(() => {
    const locale = (i18n.language || 'en').slice(0, 2);
    let active = true;
    fetch(`${API}/landing/locale-video?locale=${locale}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d?.video_url) setLocaleVideoUrl(d.video_url); })
      .catch(() => { if (active) setLocaleVideoUrl(null); });
    return () => { active = false; };
  }, [i18n.language]);

  // 2026-02 · Sync carousel height to the ProWriterGateway panel so the
  // first-row cards sit flush with "Chapter 1 · The Loom".
  const gatewayRef   = useRef(null);
  const [carouselH, setCarouselH] = useState(null);
  // Only apply fixed height on lg+ screens; mobile stacks vertically.
  const [isLgScreen, setIsLgScreen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1024);
  useLayoutEffect(() => {
    const el = gatewayRef.current;
    if (!el) return;
    const update = () => {
      const lg = window.innerWidth >= 1024;
      setIsLgScreen(lg);
      setCarouselH(lg ? el.getBoundingClientRect().height : null);
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    update();
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, []);

  // cms() helper — for English use the admin CMS override, for non-English
  // use Claude-regionalized content first, then fall back to i18n translation.
  const cms = (cmsVal, fallbackKey) => {
    if (isEnglish) return cmsVal || t(fallbackKey);
    const key = fallbackKey.replace(/^landing\./, '');
    return (regionalContent?.[key]) || t(fallbackKey);
  };
  // 2026-10 — `content`/`grand_opening` used to arrive via props from a
  // wrapper (QuillAndVineLanding) that was removed Feb-2026; nothing
  // replaced its `/api/landing` fetch, so the public route always fell
  // through to hardcoded defaults regardless of admin edits. Self-fetch
  // here (skipped when the admin live-preview supplies `content` as a
  // prop directly) fixes that without touching the preview path.
  const [fetchedLanding, setFetchedLanding] = useState(null);
  useEffect(() => {
    if (contentProp || !interactive) return;
    let active = true;
    fetch(`${API}/landing`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setFetchedLanding(d); })
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const content = contentProp ?? fetchedLanding?.content ?? null;
  const grand_opening = grandOpeningProp ?? fetchedLanding?.grand_opening ?? null;
  // iter55z-audit round 9 — First-touch UTM capture on landing.
  useEffect(() => { captureStickyUtms(); }, []);
  // 2026-02 · fire a lightweight conversion-analytics ping on mount so
  // the marketing team can measure the landing → invitation-card
  // click-through rate against `/how-we-help-writers`.
  useEffect(() => { if (interactive) trackLandingEvent('landing_view'); }, [interactive]);
  // Deep-link from `/faq` used to scroll the trust-badges strip into
  // view. The badges now live on `/how-we-help-writers`, so redirect
  // rather than trying to scroll to an element that no longer exists
  // on the landing.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash === '#landing-trust-badges') {
      window.location.replace('/how-we-help-writers#hwh-trust-badges');
    }
  }, []);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { unread: businessUnread } = useBusinessUnread({ enabled: Boolean(user) });

  // Feb-2026 — Genre preferences: fetch ranked list for logged-in users.
  const [rankedGenres, setRankedGenres] = useState([]);
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/user/genre-preferences`, { withCredentials: true })
      .then(r => setRankedGenres(r.data?.ranked || []))
      .catch(() => {});
  }, [user]);

  // Last project — powers the "Continue writing" strip.
  const [lastProject, setLastProject] = useState(null);
  useEffect(() => {
    if (!user) { setLastProject(null); return; }
    axios.get(`${API}/projects`, { withCredentials: true })
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setLastProject(list[0] || null);
      })
      .catch(() => {});
  }, [user]);

  const go = (to) => (interactive ? navigate(to) : undefined);
  /* iter-72a: unify rail destinations across every Scriptorium
   * surface (Landing, Bookstore, Login). Desk always raises the
   * Writers' Salon email modal; Vault always lands on /bookstore;
   * Margin always deep-links to /login#community. */
  const heroHeadline = isEnglish
    ? `${content?.hero_headline_part1 || 'We are where Indie writers own their business.'}${content?.hero_headline_accent ? ' ' + content.hero_headline_accent : ''}`
    : t('landing.heroHeadline');

  // Feb-2026 · Pared down to just the two sign-in paths inside the new
  // "Already writing with us?" card — "How we help writers" moved next
  // to The Desk's "draft in progress" eyebrow (TypewriterPane), and
  // "The Desk" direct-entry link was dropped as redundant with it.
  // Colour/shape now matches the "Sign the Card" button on the How We
  // Help Writers page (AuthorsPortalSection.jsx) instead of the flat
  // oxblood used elsewhere on the page.
  const loginBtnStyle = {
    borderColor: '#E57A1C',
    color:       '#ffffff',
    background:  '#E57A1C',
    boxShadow:   '0 6px 18px -8px rgba(229,122,28,0.55)',
    fontFamily:  FONTS.display,
  };
  const primaryActions = (
    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-center sm:gap-2" data-testid="landing-primary-actions">
      {interactive ? (
        <button
          type="button"
          onClick={() => go('/login')}
          data-testid="cb-nav-email-signin"
          className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition hover:brightness-105 sm:px-3.5 sm:py-1.5 sm:text-[13px]"
          style={loginBtnStyle}
        >
          <KeyRound className="h-3.5 w-3.5 shrink-0" /> {cms(content?.top_email_signin, 'landing.emailSignIn')}
        </button>
      ) : (
        <span className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[12.5px] font-medium sm:px-3.5 sm:py-1.5 sm:text-[13px]" style={loginBtnStyle}>
          <KeyRound className="h-3.5 w-3.5 shrink-0" /> {cms(content?.top_email_signin, 'landing.emailSignIn')}
        </span>
      )}
      {interactive ? (
        <Link
          to="/login"
          data-testid="cb-nav-signin"
          className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition hover:brightness-105 sm:px-3.5 sm:py-1.5 sm:text-[13px]"
          style={loginBtnStyle}
        >
          {t('landing.signInGoogle')}
        </Link>
      ) : (
        <span className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[12.5px] font-medium sm:px-3.5 sm:py-1.5 sm:text-[13px]" style={loginBtnStyle}>
          {t('landing.signInGoogle')}
        </span>
      )}
    </div>
  );

  return (
    <div
      className="relative h-full overflow-y-auto"
      style={{
        backgroundColor: P.paper,
        color:           P.ink,
        fontFamily:      FONTS.body,
      }}
      data-testid="landing-view"
    >
      {/* 2026-02 · Schema.org GEO — Organization + WebSite + SoftwareApplication.
       *  These JSON-LD blocks help AI engines (Perplexity, ChatGPT, Gemini)
       *  classify the site by language region and entity type. */}
      <StructuredData id="org-ld"  data={organizationLd({ siteUrl, lang })} />
      <StructuredData id="web-ld"  data={websiteLd({ siteUrl, lang })} />
      <StructuredData id="app-ld"  data={softwareApplicationLd({ siteUrl, lang })} />
      {/* 2026-02 · VideoObject with transcript — enables AI citation engines
       *  (Perplexity, ChatGPT, Gemini Deep Research) to reference the welcome
       *  video and attribute content to Crown & Rose in generative answers. */}
      <StructuredData
        id="video-ld"
        data={videoObjectLd({
          name:       t('video.name'),
          transcript: t('video.transcript'),
          contentUrl: localeVideoUrl || content?.mei_intro_video_url || '',
          lang,
          uploadDate: '2025-10-01',
        })}
      />
      {/* Visually-hidden transcript for traditional HTML scrapers and LLM
       *  crawlers that read the DOM rather than executing JSON-LD.
       *  `sr-only` keeps it off-screen for sighted users while keeping it
       *  fully readable by assistive technology and crawlers. */}
      <div className="sr-only" lang={lang} aria-label={t('video.name')}>
        <p>{t('video.transcript')}</p>
      </div>
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.12]" style={{ backgroundImage: GRAIN_URL }} />



      {/* iter55z-audit round 9 — Author Trust + Reader Trust gold seals.
       *  Reader-request: seals live near the content they anchor, not
       *  pinned to the viewport. Author-trust rests on the left of the
       *  "AI offerings" row; reader-trust sits next to the Promise
       *  badges just below. Both are absolutely positioned inside
       *  their own section wrappers further down (see landing-trust-badges
       *  and landing-craft-multiplier). This top-left cluster is
       *  intentionally left empty as an anchor for future overlays. */}

      {/* iter-72l — Bookmark now hangs from the top of the page (below
       * the header's The Loom button) rather than dangling inside
       * the reveal stage further down. Kept above the header via
       * z-40 on the ribbon; `top-16` matches the header base. */}


      {/* ------------------------------------------------------------------
       * Top nav — shared PublicNavBar (Home / Bookstore / Blog / How We Help / FAQ)
       * ---------------------------------------------------------------- */}
      <PublicNavBar
        onSignIn={interactive ? onEmailSignin : undefined}
        onSignUp={interactive ? onStartTrial : undefined}
      />

      {/* ── Return-to-Loom strip — only shown to signed-in writers ── */}
      {user && (
        <div
          data-testid="return-to-loom-strip"
          className="relative z-20 flex items-center gap-3 border-b px-5 py-2"
          style={{ backgroundColor: `${P.gold}18`, borderColor: `${P.gold}55` }}
        >
          <span className="text-[12.5px]" style={{ color: P.ink, fontFamily: FONTS.body }}>
            {lastProject
              ? <><strong style={{ fontFamily: FONTS.display }}>{lastProject.title || 'Untitled Project'}</strong> — pick up where you left off</>
              : <>Welcome back{user.name ? `, ${user.name.split(' ')[0]}` : ''}</>
            }
          </span>
          <Link
            to="/app"
            data-testid="return-to-loom-btn"
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-semibold transition hover:brightness-110"
            style={{ backgroundColor: P.oxblood, color: P.gold, fontFamily: FONTS.display }}
            onClick={() => trackLandingEvent('return_to_loom_click', {})}
          >
            Continue writing <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <span className="ml-auto hidden text-[11px] sm:inline" style={{ color: P.inkSoft, fontFamily: FONTS.body }}>
            Your manuscript is waiting.
          </span>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
       *  MAIN SHELF — all 9 carousels (cols 1+2) + Loom/Login (col 3)
       *  Col 3 is sticky so The Loom and sign-in stay anchored while the
       *  reader scrolls through the full carousel stack.
       * ══════════════════════════════════════════════════════════════════ */}

      {/* ── PWA install card — moved near the Welcome video in Col 3 ── */}

      <section
        className="relative z-10 w-full"
        style={{ backgroundColor: P.paperDeep }}
        data-testid="landing-main-shelf"
      >
        <div className="mx-auto max-w-7xl px-4 pt-4 pb-6 sm:px-6">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:items-start">

            {/* ── Cols 1+2: all 9 carousels, uniform 2-col sub-grid ─────── */}
            <div className="lg:col-span-2" data-testid="landing-all-carousels">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">

                <LandingBookCarousel
                  title="Recently Published"
                  fetchUrl={`${API}/bookstore/carousel-feed?category=newest&limit=16`}
                  browseHref="/bookstore"
                  visibleCount={1}
                  emptyLabel="New titles coming soon."
                  pinnedTitle="Just Once More"
                  fixedHeight={carouselH}
                />
                <LandingBookCarousel
                  title="Upcoming Reads"
                  eyebrow="Coming Soon"
                  fetchUrl={`${API}/bookstore/carousel-feed?category=upcoming&limit=16`}
                  browseHref="/bookstore"
                  visibleCount={1}
                  emptyLabel="Pre-release titles coming soon."
                  fixedHeight={carouselH}
                />
                <LandingBookCarousel
                  title="New & Upcoming Authors"
                  fetchUrl={`${API}/bookstore/carousel-feed?category=new_authors&limit=16`}
                  browseHref="/bookstore"
                  visibleCount={1}
                  emptyLabel="Debut authors coming soon."
                  pinnedTitle="The Glass Kingdom"
                  fixedHeight={carouselH}
                />
                <LandingBookCarousel
                  title="AI-Collaborated Novels"
                  eyebrow="AI"
                  fetchUrl={`${API}/bookstore/carousel-feed?category=ai&limit=16`}
                  browseHref="/bookstore"
                  visibleCount={1}
                  emptyLabel="AI-collaborated titles coming soon."
                  fixedHeight={carouselH}
                />
                {/* ── Genre carousels — ordered by user's ranked preferences ── */}
                {(function () {
                  const GENRE_DEFS = [
                    { slug: 'gothic_horror',  title: 'Gothic Horror',  eyebrow: 'New',   href: '/bookstore/g/gothic-horror',  pinnedTitle: 'The House That Breathes', emptyLabel: 'Gothic horror titles coming soon.', accentColor: '#4a8c4a', dark: 'gothic' },
                    { slug: 'dark_romance',   title: 'Dark Romance',   eyebrow: '18 +',  href: '/bookstore/g/dark-romance',   pinnedTitle: 'The Debt He Collects',    emptyLabel: 'Dark romance titles coming soon.',  accentColor: '#c0394a', dark: 'romance', gatedHref: '/bookstore/g/dark-romance' },
                    { slug: 'romance',        title: 'New Romance',    eyebrow: null,    href: '/bookstore',                  pinnedTitle: 'All the Wrong Stars',     emptyLabel: 'Romance titles coming soon.' },
                    { slug: 'fantasy',        title: 'Fantasy',        eyebrow: null,    href: '/bookstore',                  pinnedTitle: 'The Glass Kingdom',       emptyLabel: 'Fantasy titles coming soon.' },
                    { slug: 'science_fiction',title: 'Science Fiction',eyebrow: null,    href: '/bookstore',                  pinnedTitle: 'Neural Drift',            emptyLabel: 'Sci-fi titles coming soon.' },
                    { slug: 'nonfiction',     title: 'Non-Fiction',    eyebrow: null,    href: '/bookstore',                  pinnedTitle: 'Running on Empty',        emptyLabel: 'Non-fiction titles coming soon — click to learn more.', emptyImage: THINKER_IMAGE, onEmptyClick: () => setNonfictionModalOpen(true) },
                  ];
                  const preferenceOrder = rankedGenres.reduce((acc, g, i) => { acc[g] = i; return acc; }, {});
                  const sorted = [...GENRE_DEFS].sort((a, b) =>
                    (preferenceOrder[a.slug] ?? 999) - (preferenceOrder[b.slug] ?? 999)
                  );
                  return sorted.map(({ slug, title, eyebrow, href, pinnedTitle, emptyLabel, emptyImage, onEmptyClick, accentColor, dark, gatedHref }) => {
                    const carousel = (
                      <LandingBookCarousel
                        key={slug}
                        title={title}
                        eyebrow={eyebrow}
                        fetchUrl={`${API}/bookstore/carousel-feed?category=${slug}&limit=16`}
                        browseHref={href}
                        visibleCount={1}
                        emptyLabel={emptyLabel}
                        emptyImage={emptyImage}
                        onEmptyClick={onEmptyClick}
                        accentColor={accentColor}
                        gatedHref={gatedHref}
                        pinnedTitle={pinnedTitle}
                        fixedHeight={carouselH}
                      />
                    );
                    if (dark === 'gothic') return (
                      <div key={slug} className="relative overflow-hidden rounded-xl" style={{ background: 'linear-gradient(145deg, #020c02 0%, #0a1a0a 50%, #040d04 100%)', boxShadow: '0 8px 48px -8px rgba(30,90,30,0.45), 0 0 0 1px rgba(60,110,60,0.30), inset 0 0 60px rgba(15,50,15,0.45)' }}>
                        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '150px 150px' }} />
                        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(60,130,60,0.55), transparent)' }} />
                        <div className="relative z-10">{carousel}</div>
                      </div>
                    );
                    if (dark === 'romance') return (
                      <div key={slug} className="relative overflow-hidden rounded-xl" style={{ background: 'linear-gradient(145deg, #0d0508 0%, #1a0408 50%, #0a0205 100%)', boxShadow: '0 8px 48px -8px rgba(160,20,40,0.50), 0 0 0 1px rgba(200,40,60,0.30), inset 0 0 60px rgba(90,10,25,0.40)' }}>
                        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")', backgroundSize: '150px 150px' }} />
                        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,30,50,0.7), transparent)' }} />
                        <div className="relative z-10">{carousel}</div>
                      </div>
                    );
                    return carousel;
                  });
                })()}

              </div>
            </div>

            {/* ── Col 3: enlarged Loom + Login, sticky ─────────────────── */}
            <div
              className="flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start"
              data-testid="landing-row1-welcome"
            >
              {/* Chapter 1 · The Loom */}
              <div
                ref={gatewayRef}
                data-testid="scriptorium-gateway-only"
                className="rounded-xl overflow-hidden"
                style={{ boxShadow: `0 20px 50px -20px ${P.leather}55`, border: `1px solid ${P.gold}55` }}
              >
                <ProWriterGateway onDemo={onDemo} demoBusy={demoBusy} interactive={interactive} />
              </div>

              {/* Already Writing With Us + Shop link */}
              <div
                className="relative overflow-hidden rounded-xl border p-5 shadow-xl"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`,
                         boxShadow: `0 22px 40px -18px ${P.leather}55` }}
                data-testid="landing-merged-cta-card"
              >
                <div className="pointer-events-none absolute inset-0 rounded-xl opacity-[0.12]"
                     style={{ backgroundImage: GRAIN_URL }} />
                <div className="relative space-y-3">
                  <div data-testid="landing-signin-card">
                    <p className="mb-2 text-[9.5px] uppercase tracking-[0.2em]"
                       style={{ color: P.oxblood, fontFamily: FONTS.type }}>
                      {t('landing.alreadyWritingWithUs')}
                    </p>
                    {primaryActions}
                  </div>
                  <div className="border-t" style={{ borderColor: `${P.gold}44` }} />
                  <Link
                    to="/bookstore"
                    data-testid="cb-room-bookstore"
                    className="group flex items-center gap-3 transition hover:opacity-80"
                  >
                    <div
                      className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-md"
                      style={{ backgroundColor: P.oxblood, color: P.paper }}
                    >
                      <Store className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold leading-tight"
                         style={{ fontFamily: FONTS.display, color: P.ink }}>
                        {cms(content?.bs_card_title, 'landing.bookStore')}
                      </p>
                      <p className="text-[11px]" style={{ color: P.inkSoft }}>
                        {cms(content?.bs_card_eyebrow, 'landing.forReaders')}
                      </p>
                    </div>
                    <ArrowRight className="h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
                                 style={{ color: P.oxblood }} />
                  </Link>
                </div>
              </div>

              {/* Indie Reader CTA — only for signed-in users */}
              {user && (
                <div
                  className="relative overflow-hidden rounded-xl border p-4 shadow-sm"
                  style={{ backgroundColor: P.paperDeep, borderColor: P.paperBorder }}
                  data-testid="landing-reader-cta-card"
                >
                  <div className="pointer-events-none absolute inset-0 rounded-xl opacity-[0.04]"
                       style={{ backgroundImage: GRAIN_URL }} />
                  <div className="relative">
                    <p className="mb-1.5 text-[9.5px] uppercase tracking-[0.2em]"
                       style={{ color: P.oxblood, fontFamily: FONTS.type }}>
                      Read anywhere
                    </p>
                    <p className="text-[12px] mb-3 leading-snug" style={{ color: P.inkSoft, fontFamily: FONTS.body }}>
                      Open your library in the Indie Reader app — all your purchased titles, one tap.
                    </p>
                    <ReadInReaderBtn
                      variant="ghost"
                      label="Open Indie Reader"
                      data-testid="landing-reader-open-btn"
                      className="w-full [&>button]:w-full [&>button]:justify-center"
                    />
                  </div>
                </div>
              )}

              {/* Welcome video — compact, below the cards */}
              <div
                className="hidden lg:block rounded-xl overflow-hidden border"
                style={{ borderColor: `${P.gold}44` }}
                data-testid="landing-welcome-video-band"
              >
                <WelcomeVideoBand content={content} compact={true} localeVideoUrl={localeVideoUrl} />
              </div>

              {/* PWA install card — below the Welcome video */}
              <div data-testid="landing-pwa-card-slot">
                <PwaInstallCard />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Pricing CTA ribbon */}
      <div
        className="relative z-10 border-y py-2.5 text-center"
        style={{ backgroundColor: `${P.oxblood}08`, borderColor: `${P.gold}55` }}
        data-testid="pricing-cta-ribbon"
      >
        <div
          className="inline-flex flex-wrap items-center justify-center gap-1.5 text-[12px] font-medium"
          style={{ color: P.leather, fontFamily: FONTS.body }}
        >
          <span style={{ color: P.inkSoft }}>{t('landing.pricingPlans')}</span>
          <span style={{ color: P.goldDeep }}>·</span>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 transition-opacity hover:opacity-80"
            style={{ color: P.leather }}
            data-testid="pricing-ribbon-link"
          >
            <span>{t('landing.pricingStartingAt')}</span>
            <span
              className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold"
              style={{ backgroundColor: '#E57A1C18', color: '#E57A1C' }}
            >
              {t('landing.seePricing')}
            </span>
          </Link>
          <span style={{ color: P.goldDeep }}>·</span>
          <Link
            to="/compare"
            className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-opacity hover:opacity-80"
            style={{ backgroundColor: '#E57A1C15', color: '#E57A1C' }}
            data-testid="compare-ribbon-link"
          >
            {t('landing.compareOthers')}
          </Link>
        </div>
      </div>

      {/* Competitor name-drop strip */}
      <Link
        to="/compare"
        data-testid="competitor-namecheck-strip"
        className="relative z-10 block w-full border-b py-2 text-center transition-opacity hover:opacity-80"
        style={{ backgroundColor: `${P.gold}0D`, borderColor: `${P.gold}33` }}
      >
        <p className="text-[11.5px] tracking-wide" style={{ color: P.inkSoft, fontFamily: FONTS.body }}>
          {t('landing.everythingYouNeed')}{' '}
          <span
            className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10.5px] font-semibold"
            style={{ backgroundColor: '#E57A1C18', color: '#E57A1C' }}
          >
            {t('landing.allInOneLine')}
          </span>
        </p>
      </Link>

      {/* Non-Fiction Coming Soon modal */}
      <NonfictionComingSoonModal
        open={nonfictionModalOpen}
        onClose={() => setNonfictionModalOpen(false)}
      />

      {/* The Desk — brand copy block, centred below carousels per user request */}
      <section
        className="relative z-10 border-t"
        style={{ borderColor: `${P.gold}33`, backgroundColor: P.paper }}
        data-testid="landing-desk-tagline"
      >
        <div
          className="mx-auto max-w-4xl px-4 py-8 sm:px-6 text-center"
          data-testid="scriptorium-hero-main-band"
        >
          <TypewriterPane
            headline={heroHeadline}
            subline={cms(content?.hero_subline, 'landing.heroSubline')}
            safetyNote={cms(content?.hero_safety_note, 'landing.heroSafetyNote')}
          />
        </div>
        <div
          className="mx-auto max-w-3xl px-6 pb-8 text-center"
          data-testid="hero-royalty-line"
        >
          <p
            className="text-base sm:text-lg leading-relaxed"
            style={{ color: P.inkSoft, fontFamily: FONTS.serif }}
          >
            {cms(content?.hero_royalty_line, 'landing.heroRoyaltyLine')}
          </p>
        </div>
      </section>

      {/* iter-p3-w — Live community ticker, Authors' Portal doorway,
       * and ticket rail all moved to `/how-we-help-writers` to keep
       * the landing focused on Try/Buy. Kept the small unread-stack
       * footer here as an ambient signal. */}
      <div className="relative z-10">
        <UnreadStackFooter books={[]} />
      </div>

      {/* iter55f63 — Small "Staff" link mirroring the one on the
       * bookstore landing footer; points to the The Loom admin
       * login (`/admin`). Deliberately low-key: opacity 0.55, small
       * type, lower-right, so casual visitors don't spot it but staff
       * always know where to click. */}
      <footer
        className="relative z-10 border-t px-6 py-4 text-[10.5px]"
        style={{ borderColor: `${P.gold}55`, backgroundColor: P.paperDeep, color: P.inkSoft }}
      >
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span>© Crown &amp; Rose · {t('landing.allRightsReserved')}</span>
            <Link
              to="/how-we-help-writers#hwh-ohas"
              data-testid="landing-ohas-badge"
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider hover:opacity-80"
              style={{ backgroundColor: '#8b5cf6', color: '#fff' }}
              title="Open Human Authorship Standards · D3 (Formal Audit)"
            >
              OHAS · D3
            </Link>
            <Link
              to="/how-we-help-writers"
              data-testid="landing-footer-how-we-help"
              className="hover:opacity-100 opacity-80"
              style={{ color: P.inkSoft }}
            >
              {t('landing.howWeHelp')}
            </Link>
            <Link
              to="/what-we-offer"
              data-testid="landing-footer-what-we-offer"
              className="hover:opacity-100 opacity-80"
              style={{ color: P.inkSoft }}
            >
              {t('landing.whatWeOffer')}
            </Link>
            <Link
              to="/compare"
              data-testid="landing-footer-compare"
              className="hover:opacity-100 opacity-80"
              style={{ color: P.inkSoft }}
            >
              Compare
            </Link>
            <Link
              to={blogPath}
              data-testid="landing-footer-blog"
              className="hover:opacity-100 opacity-80"
              style={{ color: P.inkSoft }}
            >
              Blog
            </Link>
            <Link
              to="/faq"
              data-testid="landing-footer-faq"
              className="hover:opacity-100 opacity-80"
              style={{ color: P.inkSoft }}
            >
              {t('landing.faq')}
            </Link>
            <CommunityFundTicker />
          </div>
          <Link
            to="/admin"
            data-testid="scriptorium-staff-link"
            className="opacity-55 hover:opacity-100"
            style={{ color: P.inkSoft, fontFamily: FONTS.display, letterSpacing: '0.14em' }}
          >
            {t('landing.staff')}
          </Link>
        </div>
        <PoweredByEmergent testId="scriptorium-powered-by-emergent" />
      </footer>
    </div>
  );
}
